import { state } from '../state.js';
import { showToast } from '../utils/toast.js';

let isInitialized = false;
let originalFetch: typeof window.fetch | null = null;

function setAuthPimUrl(pimUrl: string | null): void {
  state.auth.pimUrl = pimUrl;
  if (pimUrl) {
    try {
      state.auth.tenantId = new URL(pimUrl).hostname;
    } catch {
      state.auth.tenantId = null;
    }
  } else {
    state.auth.tenantId = null;
  }
}

/**
 * Configure global fetch interceptor to attach Bearer token fallback for third-party cookie restrictions
 */
export function setupFetchInterceptor(): void {
  if (typeof window === 'undefined' || originalFetch) return;

  originalFetch = window.fetch;
  window.fetch = async (input: RequestInfo | URL, init: RequestInit = {}) => {
    let url = '';
    if (typeof input === 'string') {
      url = input;
    } else if (input instanceof URL) {
      url = input.toString();
    } else if (input && typeof (input as Request).url === 'string') {
      url = (input as Request).url;
    }

    // Attach credentials include and Authorization Bearer header for /api/ calls
    const isApiCall = url.includes('/api/');
    if (isApiCall) {
      const headers = new Headers(init.headers || (input instanceof Request ? input.headers : {}));
      if (state.auth?.sessionToken && !headers.has('Authorization')) {
        headers.set('Authorization', `Bearer ${state.auth.sessionToken}`);
      }
      // Tag all API calls so the server can distinguish legitimate SPA polls from
      // unauthenticated crawlers / scanners and suppress noisy 401 logs accordingly.
      if (!headers.has('X-Akeneo-Tool-Client')) {
        headers.set('X-Akeneo-Tool-Client', '1');
      }

      const enhancedInit: RequestInit = {
        ...init,
        headers,
        credentials: init.credentials || 'include',
      };

      return originalFetch!(input, enhancedInit);
    }

    return originalFetch!(input, init);
  };
}

/**
 * Initialize Iframe SSO & PostMessage JWT Listener
 */
export async function initIframeAuth(options: Record<string, unknown> = {}): Promise<void> {
  setupFetchInterceptor();

  if (!isInitialized && typeof window !== 'undefined') {
    window.addEventListener('message', handleParentMessage);
    isInitialized = true;
  }

  // Restore saved token from localStorage if available
  if (typeof localStorage !== 'undefined' && !state.auth.sessionToken) {
    const savedToken = localStorage.getItem('akeneo_session_token');
    if (savedToken) {
      state.auth.sessionToken = savedToken;
    }
  }

  // Check auth config from backend
  try {
    const configRes = await fetch('/api/v1/auth/config');
    if (configRes.ok) {
      const configData = await configRes.json();
      state.auth.authMode = configData.authMode || 'akeneo_jwt';
      state.auth.authBypass = Boolean(configData.authBypass);
      state.auth.isEmbeddedRequired = Boolean(configData.isEmbeddedRequired);
      state.auth.enableDevHarness = configData.enableDevHarness !== undefined ? Boolean(configData.enableDevHarness) : true;
    }
  } catch {
    // Default fallback
  }

  // If auth bypass is enabled, synthesize authenticated admin user immediately
  if (state.auth.authBypass || state.auth.authMode === 'disabled') {
    state.auth.isAuthenticated = true;
    state.auth.user = {
      id: 'anonymous',
      username: 'anonymous_admin',
      roles: ['admin'],
    };
    state.auth.handshakeStatus = 'authenticated';
    renderAuthUI();
    return;
  }

  // If Akeneo initiated activation with pim_url query parameter on root URL, redirect to oauth/connect
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    const pimUrl = urlParams.get('pim_url') || urlParams.get('instance_url');
    if (pimUrl) {
      window.location.href = `/oauth/connect${window.location.search}`;
      return;
    }
  }

  // Check existing session
  const hasSession = await checkAuthSession();

  if (!hasSession) {
    if (state.isEmbedded) {
      requestJwt();
    } else {
      renderAuthUI();
    }
  } else {
    renderAuthUI();
  }
}

/**
 * Send request_jwt message to Akeneo PIM parent window
 */
export function requestJwt(targetOrigin: string = '*'): void {
  if (typeof window === 'undefined') return;

  state.auth.handshakeStatus = 'requesting';
  renderAuthUI();

  try {
    if (window.parent && window.parent !== window) {
      window.parent.postMessage({ type: 'request_jwt' }, targetOrigin);
    } else {
      // Also post to self in case iframe test harness is within same context
      window.postMessage({ type: 'request_jwt' }, targetOrigin);
    }
  } catch (err) {
    console.warn('[iframe-auth] Could not send postMessage to parent window:', err);
  }

  // Set timeout for handshake response
  setTimeout(() => {
    if (state.auth.handshakeStatus === 'requesting' && !state.auth.isAuthenticated) {
      state.auth.handshakeStatus = 'failed';
      renderAuthUI();
    }
  }, 5000);
}

/**
 * Handle incoming postMessage events from Akeneo PIM parent
 */
export function handleParentMessage(event: MessageEvent): void {
  if (!event || !event.data) {
    return;
  }

  let data = event.data;
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data);
    } catch {
      // Raw string, not JSON
    }
  }

  if (!data || typeof data !== 'object') {
    return;
  }

  const msgType = String(data.type || data.action || '').toUpperCase();
  const token = data.token || data.jwt || data.jwtToken;

  if (
    (msgType === 'JWT_TOKEN' || msgType === 'JWT' || msgType === 'TOKEN') &&
    typeof token === 'string' &&
    token.trim().length > 0
  ) {
    exchangeJwtToken(token.trim());
  }
}

/**
 * Exchange Akeneo JWT token for an application session
 */
export async function exchangeJwtToken(jwt: string): Promise<boolean> {
  if (!jwt || typeof jwt !== 'string') {
    state.auth.handshakeStatus = 'failed';
    state.auth.errorMessage = 'JWT token is missing or empty';
    renderAuthUI();
    return false;
  }

  try {
    const res = await fetch('/api/v1/auth/akeneo-jwt', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token: jwt }),
    });

    if (!res.ok) {
      const errData = await res.json().catch(() => ({}));
      state.auth.handshakeStatus = 'failed';
      state.auth.errorMessage = errData.message || `Auth failed with HTTP ${res.status}`;
      renderAuthUI();
      showToast(`Akeneo SSO Failed: ${state.auth.errorMessage}`, 'error');
      return false;
    }

    const authData = await res.json();
    state.auth.isAuthenticated = true;
    state.auth.sessionToken = authData.sessionToken;
    state.auth.user = authData.user || null;
    setAuthPimUrl(authData.pimUrl || null);
    state.auth.handshakeStatus = 'authenticated';
    state.auth.errorMessage = null;

    if (typeof localStorage !== 'undefined' && authData.sessionToken) {
      localStorage.setItem('akeneo_session_token', authData.sessionToken);
    }

    renderAuthUI();

    // Dispatch custom auth success event
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('akeneo:authenticated', {
          detail: { user: authData.user, pimUrl: authData.pimUrl },
        })
      );
    }

    const userName = authData.user?.username || authData.user?.id || 'Akeneo User';
    showToast(`Authenticated via Akeneo SSO as ${userName}`, 'success');
    return true;
  } catch (err: any) {
    state.auth.handshakeStatus = 'failed';
    state.auth.errorMessage = err?.message || 'Network error during token exchange';
    renderAuthUI();
    showToast(`SSO Connection Error: ${state.auth.errorMessage}`, 'error');
    return false;
  }
}

/**
 * Check if the current session is valid
 */
export async function checkAuthSession(): Promise<boolean> {
  try {
    const headers: Record<string, string> = {};
    if (state.auth.sessionToken) {
      headers['Authorization'] = `Bearer ${state.auth.sessionToken}`;
    }

    const res = await fetch('/api/v1/auth/session', { headers });
    if (res.ok) {
      const data = await res.json();
      if (data.authenticated && data.session) {
        state.auth.isAuthenticated = true;
        state.auth.user = data.session.user || null;
        setAuthPimUrl(data.session.pimUrl || null);
        state.auth.handshakeStatus = 'authenticated';
        renderAuthUI();
        return true;
      }
    }
  } catch {
    // Session invalid or network failure
  }

  return false;
}

/**
 * Log out user and destroy session
 */
export async function logoutUser(): Promise<void> {
  try {
    const headers: Record<string, string> = {};
    if (state.auth.sessionToken) {
      headers['Authorization'] = `Bearer ${state.auth.sessionToken}`;
    }
    await fetch('/api/v1/auth/logout', { method: 'POST', headers });
  } catch {
    // Ignore error
  }

  state.auth.isAuthenticated = false;
  state.auth.sessionToken = null;
  state.auth.user = null;
  state.auth.handshakeStatus = 'idle';

  if (state.pollTimer) {
    clearInterval(state.pollTimer);
    state.pollTimer = null;
  }

  if (typeof localStorage !== 'undefined') {
    localStorage.removeItem('akeneo_session_token');
  }

  renderAuthUI();
  showToast('Logged out of Akeneo SSO Session', 'info');
}

/**
 * Return authorization header object for external calls
 */
export function getAuthHeader(): Record<string, string> {
  if (state.auth.sessionToken) {
    return { Authorization: `Bearer ${state.auth.sessionToken}` };
  }
  return {};
}

/**
 * Update UI elements reflecting authentication state
 */
export function renderAuthUI(): void {
  if (typeof document === 'undefined') return;

  const appContainer = document.getElementById('app');
  const authBadge = document.getElementById('auth-user-badge');
  const authUserName = document.getElementById('auth-user-name');
  const authLockScreen = document.getElementById('auth-locked-screen');
  const authStatusIndicator = document.getElementById('auth-status-indicator');

  // Update header auth pill / badge
  if (authBadge && authUserName) {
    if (state.auth.isAuthenticated && state.auth.user) {
      authBadge.style.display = 'inline-flex';
      authUserName.textContent = state.auth.user.username || state.auth.user.id || 'Akeneo User';
      authBadge.title = `Authenticated user (${state.auth.user.email || state.auth.user.username})`;
    } else {
      authBadge.style.display = 'none';
    }
  }

  if (authStatusIndicator) {
    if (state.auth.isAuthenticated) {
      authStatusIndicator.textContent = state.auth.authBypass ? 'Bypass Active' : 'SSO Active';
      authStatusIndicator.className = 'badge badge-success';
    } else if (state.auth.handshakeStatus === 'requesting') {
      authStatusIndicator.textContent = 'Authenticating...';
      authStatusIndicator.className = 'badge badge-warning';
    } else {
      authStatusIndicator.textContent = 'Not Authenticated';
      authStatusIndicator.className = 'badge badge-secondary';
    }
  }

  // Display locked / setup guide screen if not bypass and not authenticated in standalone mode
  const isBypass = state.auth.authBypass || state.auth.authMode === 'disabled';
  const showSetupScreen = !isBypass && !state.auth.isAuthenticated && !state.isEmbedded;

  if (authLockScreen) {
    authLockScreen.style.display = showSetupScreen ? 'flex' : 'none';
  }

  // Defer/hide main application dashboard container if setup screen is active, otherwise display it
  if (appContainer) {
    appContainer.style.display = showSetupScreen ? 'none' : 'flex';
  }

  // Populate dynamic URLs in the setup screen if available
  if (showSetupScreen && typeof window !== 'undefined') {
    const origin = window.location.origin || `http://${window.location.host || 'localhost:3000'}`;
    const setupAppUrl = document.getElementById('setup-app-url');
    const setupCallbackUrl = document.getElementById('setup-callback-url');
    const setupWebhookUrl = document.getElementById('setup-webhook-url');

    if (setupAppUrl) setupAppUrl.textContent = `${origin}/`;
    if (setupCallbackUrl) setupCallbackUrl.textContent = `${origin}/api/v1/oauth/callback`;
    if (setupWebhookUrl) setupWebhookUrl.textContent = `${origin}/api/v1/webhooks/akeneo`;
  }
}
